// Panel manifest ES256 signature verification — the browser mirror of
// cmd/authority/panel_manifest_sign.go (SPEC-panel-manifest-registry-v0 §"Signing scheme").
// Proto3 disclaims canonical bytes, so we sign a FIXED-TUPLE PROJECTION — the same shape JWT
// detached signatures / SSH host keys / Sigstore / TUF use — which is deterministic across
// languages. signature = ECDSA-P256 over SHA256(canonical_bytes), 64-byte r||s.
//
// WebCrypto hashes the message itself, so pass the canonical bytes UNHASHED; that matches Go's
// ecdsa.Sign(hash) shape.

import { getTracer } from './otel.js';

const TEXT_ENCODER = new TextEncoder();

// sha256OfString — raw 32 hash bytes of a UTF-8 string (Go: sha256Hex).
async function sha256OfString(s) {
    const bytes = TEXT_ENCODER.encode(s || '');
    const digest = await crypto.subtle.digest('SHA-256', bytes);
    return new Uint8Array(digest);
}

// u64Be — big-endian 8 bytes (Go: binary.BigEndian.PutUint64); JS numbers are safe to 2^53.
function u64Be(value) {
    const out = new Uint8Array(8);
    const view = new DataView(out.buffer);
    view.setBigUint64(0, BigInt(value), false);
    return out;
}

// sortedJoin — UNUSED. capsDigest replaced it: sort + "\n"-join is NOT injective (see below).
function sortedJoin(arr) {
    if (!Array.isArray(arr) || arr.length === 0) return '';
    return [...arr].sort().join('\n');
}

// m2DepsDigest / capsDigest — the INJECTIVE commitments (models/val_m2deps_injective.qnt). Each
// item is length-framed — u32be(utf8len) ++ bytes (plus u64be(bv) for a dep pin) — then BYTE-sorted
// (language-agnostic; no unicode string sort), count-framed with u32be(n), then SHA-256'd.
// Prefix-free is the point: the old `type_uri@bv`-join and sortedJoin were malleable, so a value
// carrying the delimiter forged an element boundary and ONE signature covered TWO sets.
// Byte-identical to the Haskell + Swift implementations.
async function capsDigest(caps) {
    const list = Array.isArray(caps) ? caps : [];
    const enc = list.map((c) => {
        const s = new TextEncoder().encode(c || '');
        const out = new Uint8Array(4 + s.length);
        new DataView(out.buffer).setUint32(0, s.length, false);
        out.set(s, 4);
        return out;
    });
    enc.sort((x, y) => {
        const n = Math.min(x.length, y.length);
        for (let i = 0; i < n; i++) if (x[i] !== y[i]) return x[i] - y[i];
        return x.length - y.length;
    });
    const total = 4 + enc.reduce((a, e) => a + e.length, 0);
    const framed = new Uint8Array(total);
    new DataView(framed.buffer).setUint32(0, list.length, false);
    let off = 4;
    for (const e of enc) { framed.set(e, off); off += e.length; }
    return new Uint8Array(await crypto.subtle.digest('SHA-256', framed));
}

async function m2DepsDigest(deps) {
    const list = Array.isArray(deps) ? deps : [];
    const enc = list.map((d) => {
        const uri = new TextEncoder().encode(d.type_uri || '');
        const out = new Uint8Array(4 + uri.length + 8);
        const dv = new DataView(out.buffer);
        dv.setUint32(0, uri.length, false);   // u32be(len)
        out.set(uri, 4);
        dv.setBigUint64(4 + uri.length, BigInt(d.breaking_version || 0), false); // u64be(bv)
        return out;
    });
    // byte-lexicographic sort of the per-pin encodings
    enc.sort((x, y) => {
        const n = Math.min(x.length, y.length);
        for (let i = 0; i < n; i++) if (x[i] !== y[i]) return x[i] - y[i];
        return x.length - y.length;
    });
    const total = 4 + enc.reduce((a, e) => a + e.length, 0);
    const framed = new Uint8Array(total);
    new DataView(framed.buffer).setUint32(0, list.length, false); // u32be(count)
    let off = 4;
    for (const e of enc) { framed.set(e, off); off += e.length; }
    const hash = await crypto.subtle.digest('SHA-256', framed);
    return new Uint8Array(hash);
}

// panelManifestCanonicalBytes — the 384-byte canonical tuple from a manifest's signed fields:
//   SHA256(panel_id) || SHA256(display_name) || SHA256(icon_id) || u64be(manifest_version)
//   || SHA256(bundle_hash) || u64be(min_runtime_version) || u64be(min_substrate_version)
//   || u64be(signed_at) || SHA256(issuer) || capsDigest(required) || capsDigest(optional)
//   || SHA256(container_class) || SHA256(bridge_predicate) || m2DepsDigest(m2_deps)
// container_class + bridge_predicate joined at the TAIL (256 -> 384 bytes) because dropping them
// on the static->discovered path was a real regression; old verifiers reject the new shape, per
// the SPEC's forward-extensibility rule.
export async function panelManifestCanonicalBytes(fields) {
    const parts = [
        await sha256OfString(fields.panel_id),
        await sha256OfString(fields.display_name),
        await sha256OfString(fields.icon_id),
        u64Be(fields.manifest_version || 0),
        await sha256OfString(fields.bundle_hash),
        u64Be(fields.min_runtime_version || 0),
        u64Be(fields.min_substrate_version || 0),
        u64Be(fields.signed_at || 0),
        await sha256OfString(fields.issuer),
        await capsDigest(fields.required_capabilities),   // INJECTIVE framed digest
        await capsDigest(fields.optional_capabilities),
        await sha256OfString(fields.container_class),
        await sha256OfString(fields.bridge_predicate),
        // seam 8: the metamodel binding, appended at the tail.
        await sha256OfString(fields.m2_contribution_hash || ''),
        await m2DepsDigest(fields.m2_deps),   // INJECTIVE framed digest (already 32B)
    ];
    let total = 0;
    for (const p of parts) total += p.byteLength;
    const out = new Uint8Array(total);
    let offset = 0;
    for (const p of parts) {
        out.set(p, offset);
        offset += p.byteLength;
    }
    return out;
}

// verifyPanelManifestSignature — ECDSA P-256 verify of a 64-byte r||s (IEEE P1363) signature over
// the canonical tuple. publicKey is a CryptoKey (see importEcdsaP256PublicKeyJwk). Returns a
// Promise<boolean> — false on a bad signature; it throws only on argument-shape errors.
export async function verifyPanelManifestSignature(publicKey, fields, signature) {
    if (!(signature instanceof Uint8Array) || signature.byteLength !== 64) {
        throw new Error('verifyPanelManifestSignature: signature must be Uint8Array(64) [r||s]');
    }
    const canonical = await panelManifestCanonicalBytes(fields);
    return crypto.subtle.verify(
        { name: 'ECDSA', hash: 'SHA-256' },
        publicKey,
        signature,
        canonical,
    );
}

// importEcdsaP256PublicKeyJwk — import { kty:'EC', crv:'P-256', x, y, kid, alg:'ES256' } from a
// JWKS entry into a CryptoKey for verifyPanelManifestSignature.
export async function importEcdsaP256PublicKeyJwk(jwk) {
    if (!jwk || jwk.kty !== 'EC' || jwk.crv !== 'P-256') {
        throw new Error('importEcdsaP256PublicKeyJwk: expected EC P-256 JWK');
    }
    return crypto.subtle.importKey(
        'jwk',
        jwk,
        { name: 'ECDSA', namedCurve: 'P-256' },
        false,  // not extractable; only used for verify
        ['verify'],
    );
}

// findJwkByKid — a manifest's `issuer` is the signing JWK's kid, so verifiers fetch the JWKS once
// and index by kid instead of re-fetching per manifest. Returns the JWK or null.
export function findJwkByKid(jwks, kid) {
    if (!jwks || !Array.isArray(jwks.keys)) return null;
    for (const k of jwks.keys) {
        if (k && k.kid === kid) return k;
    }
    return null;
}

// ─── PanelManifest proto3 decode ────────────────────────────────────
// Hand-rolled (no JS proto codegen yet). Fields per render.proto: panel_id 1, display_name 2,
// icon_id 3, manifest_version 4, bundle_hash 5, min_runtime_version 6, min_substrate_version 7,
// required_capabilities 8, optional_capabilities 9, issuer 10, signature 11, signed_at 12,
// container_class 13, bridge_predicate 14.

const TEXT_DECODER = new TextDecoder('utf-8');

// parseM2Dep — the embedded { string type_uri = 1; uint32 breaking_version = 2 } message.
function parseM2Dep(bytes) {
    const dep = { type_uri: '', breaking_version: 0 };
    let i = 0;
    const n = bytes.length;
    while (i < n) {
        let tag = 0, shift = 0;
        while (i < n) {
            const b = bytes[i++];
            tag |= (b & 0x7f) << shift;
            if ((b & 0x80) === 0) break;
            shift += 7;
        }
        const fieldNum = tag >>> 3;
        const wireType = tag & 0x07;
        if (wireType === 2) {
            let len = 0, lShift = 0;
            while (i < n) {
                const b = bytes[i++];
                len |= (b & 0x7f) << lShift;
                if ((b & 0x80) === 0) break;
                lShift += 7;
            }
            const slice = bytes.slice(i, i + len);
            if (fieldNum === 1) dep.type_uri = TEXT_DECODER.decode(slice);
            i += len;
        } else if (wireType === 0) {
            let v = 0, vShift = 0;
            while (i < n) {
                const b = bytes[i++];
                v += (b & 0x7f) * Math.pow(2, vShift);
                if ((b & 0x80) === 0) break;
                vShift += 7;
            }
            if (fieldNum === 2) dep.breaking_version = v;
        }
    }
    return dep;
}

export function parsePanelManifest(bytes) {
    const out = {
        panel_id: '',
        display_name: '',
        icon_id: '',
        manifest_version: 0,
        bundle_hash: '',
        min_runtime_version: 0,
        min_substrate_version: 0,
        required_capabilities: [],
        optional_capabilities: [],
        issuer: '',
        signature: new Uint8Array(0),
        signed_at: 0,
        container_class: '',
        bridge_predicate: '',
        m2_contribution_hash: '',
        m2_deps: [],
    };
    let i = 0;
    const n = bytes.length;
    while (i < n) {
        let tag = 0, shift = 0;
        while (i < n) {
            const b = bytes[i++];
            tag |= (b & 0x7f) << shift;
            if ((b & 0x80) === 0) break;
            shift += 7;
        }
        const fieldNum = tag >>> 3;
        const wireType = tag & 0x07;

        if (wireType === 2) {
            let len = 0, lShift = 0;
            while (i < n) {
                const b = bytes[i++];
                len |= (b & 0x7f) << lShift;
                if ((b & 0x80) === 0) break;
                lShift += 7;
            }
            const valueEnd = i + len;
            const slice = bytes.slice(i, valueEnd);
            switch (fieldNum) {
                case 1: out.panel_id = TEXT_DECODER.decode(slice); break;
                case 2: out.display_name = TEXT_DECODER.decode(slice); break;
                case 3: out.icon_id = TEXT_DECODER.decode(slice); break;
                case 5: out.bundle_hash = TEXT_DECODER.decode(slice); break;
                case 8: out.required_capabilities.push(TEXT_DECODER.decode(slice)); break;
                case 9: out.optional_capabilities.push(TEXT_DECODER.decode(slice)); break;
                case 10: out.issuer = TEXT_DECODER.decode(slice); break;
                case 11: out.signature = new Uint8Array(slice); break;
                case 13: out.container_class = TEXT_DECODER.decode(slice); break;
                case 14: out.bridge_predicate = TEXT_DECODER.decode(slice); break;
                case 15: out.m2_contribution_hash = TEXT_DECODER.decode(slice); break;
                case 16: out.m2_deps.push(parseM2Dep(slice)); break;  // embedded M2Dep
            // Unknown LEN fields: skip.
            }
            i = valueEnd;
        } else if (wireType === 0) {
            let v = 0, vShift = 0;
            while (i < n) {
                const b = bytes[i++];
                v += (b & 0x7f) * Math.pow(2, vShift);
                if ((b & 0x80) === 0) break;
                vShift += 7;
            }
            switch (fieldNum) {
                case 4: out.manifest_version = v; break;
                case 6: out.min_runtime_version = v; break;
                case 7: out.min_substrate_version = v; break;
                case 12: out.signed_at = v; break;
            // Unknown varint fields: skip.
            }
        } else if (wireType === 1) {
            i += 8;  // fixed64
        } else if (wireType === 5) {
            i += 4;  // fixed32
        } else {
            break;  // unknown wire type — abort
        }
    }
    return out;
}

// ─── High-level verify path ─────────────────────────────────────────

// verifyManifest — decode PanelManifest bytes and verify the signature against the JWK matching the
// manifest's issuer (kid). Returns { fields, valid, error? }; NEVER throws on a bad signature, so
// callers skip the panel and log. Pure — the JWKS must be pre-fetched by the caller.
// Emits the SPEC #800 manifest.verify span with verify.outcome ∈ {pass, signature_fail,
// not_in_registry}.
export async function verifyManifest(manifestBytes, jwks) {
    const fields = parsePanelManifest(manifestBytes);
    const tracer = getTracer();
    const span = (tracer && typeof tracer.startSpan === 'function')
        ? tracer.startSpan('manifest.verify', {
            attributes: { 'panel.id': fields.panel_id || '' },
        })
        : null;
    function done(result, outcome) {
        if (span) {
            try {
                span.setAttribute('verify.outcome', outcome);
                span.end();
            } catch (_) { /* tracer errors don't break verify */ }
        }
        return result;
    }
    if (!fields.panel_id) {
        return done({ fields, valid: false, error: 'missing panel_id' }, 'signature_fail');
    }
    if (!fields.issuer) {
        return done({ fields, valid: false, error: 'missing issuer (kid)' }, 'signature_fail');
    }
    if (!fields.signature || fields.signature.byteLength !== 64) {
        return done({ fields, valid: false, error: 'signature must be 64-byte r||s' }, 'signature_fail');
    }
    const jwk = findJwkByKid(jwks, fields.issuer);
    if (!jwk) {
        return done({ fields, valid: false, error: 'no JWK matches issuer kid: ' + fields.issuer }, 'not_in_registry');
    }
    let publicKey;
    try {
        publicKey = await importEcdsaP256PublicKeyJwk(jwk);
    } catch (e) {
        return done({ fields, valid: false, error: 'JWK import failed: ' + (e && e.message ? e.message : e) },
          'signature_fail');
    }
    let valid = false;
    try {
        valid = await verifyPanelManifestSignature(publicKey, fields, fields.signature);
    } catch (e) {
        return done({ fields, valid: false, error: 'signature verify threw: ' + (e && e.message ? e.message : e) },
          'signature_fail');
    }
    return valid
        ? done({ fields, valid: true }, 'pass')
        : done({ fields, valid: false, error: 'signature does not match canonical tuple' }, 'signature_fail');
}

// fetchAndVerifyBundle — fetch the panel's WASM bundle from
// <base_url>/v2/<bundle_repo>/blobs/sha256:<bundle_hash> and verify the bytes against the
// manifest's bundle_hash before instantiate. bundle_repo is a manifest field (decoupled from
// panel_id so a panel can move repos); bundle_hash is bare hex, the `sha256:` prefix is OCI syntax.
// Why base_url fronts an anon adapter rather than Harbor directly:
// docs/design/PANEL-SIGNING-AND-ROTATION.md §5.7. A hash MISMATCH is a security boundary — it throws, and
// callers MUST abort instantiation.
export async function fetchAndVerifyBundle(bundleRepo, bundleHash, baseUrl) {
    const base = baseUrl || 'https://cdn.coglative.com';
    // A repo path like "wasm-public/com.coglative.demo.fleet": encode each segment, keep the slashes.
    const encodedRepo = bundleRepo.split('/').map(encodeURIComponent).join('/');
    const url = `${base}/v2/${encodedRepo}/blobs/sha256:${encodeURIComponent(bundleHash.toLowerCase())}`;
    const response = await fetch(url);
    if (!response.ok) {
        throw new Error(`fetchAndVerifyBundle: HTTP ${response.status} ${response.statusText} from ${url}`);
    }
    const bytes = new Uint8Array(await response.arrayBuffer());
    // bundle_hash is lowercase hex per SPEC.
    const digest = await crypto.subtle.digest('SHA-256', bytes);
    const digestHex = bytesToHex(new Uint8Array(digest));
    if (digestHex !== bundleHash.toLowerCase()) {
        throw new Error(
            `fetchAndVerifyBundle: bundle_hash mismatch — ` +
            `manifest declared ${bundleHash}, fetched bytes hash ${digestHex}`,
        );
    }
    return bytes;
}

function bytesToHex(bytes) {
    let out = '';
    for (let i = 0; i < bytes.length; i++) {
        out += bytes[i].toString(16).padStart(2, '0');
    }
    return out;
}
